import { FC, useMemo } from "react";
import { Formik, FormikHelpers } from "formik";
import { useSearchParams } from "react-router-dom";
import { utils } from "@vilocnv/allsetra-core";
import InnerForm from "./children/InnerForm";

// Data
import { useAppDispatch } from "hooks";
import {
  createManualRideInitialValues,
  createManualRideValidationSchema,
  transformCreateManualRideForApi,
  ICreateManualRideFormValues
} from "app/data/helpers/createManualRideHelpers";
import {
  createObjectRideThunk,
  getObjectRidesByQueryThunk,
  updateObjectRidesSilently
} from "app/features";
import { ObjectsService } from "app/data/services";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  accountId: string;
  objectId: string;
}

const extractRideIdFromLocationHeader = (
  locationHeader?: string | null
): string | null => {
  if (!locationHeader) return null;
  const segments = locationHeader.split("/").filter(Boolean);
  return segments[segments.length - 1] || null;
};

const AddManualRideForm: FC<Props> = ({
  open,
  onClose,
  accountId,
  objectId
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const initialValues = useMemo(() => createManualRideInitialValues, []);

  const buildSearchParams = () => {
    const page = parseInt(searchParams.get("page") || "1", 10);
    const itemsPerPage = parseInt(
      searchParams.get("itemsPerPage") ||
        String(utils.getCommonParamsForApi().itemsPerPage || 25),
      10
    );
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const search = searchParams.get("search");

    const params: any = {
      page,
      itemsPerPage,
      includeRidesWithoutDriver: true,
      startedWithoutIgnition: true,
      orderBy: [{ field: "startDate", ascending: true }]
    };

    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (search) params.search = search;

    return params;
  };

  const refreshRides = async (rideIdToFind?: string | null) => {
    const params = buildSearchParams();

    await dispatch(
      getObjectRidesByQueryThunk({
        params,
        accountId,
        objectId
      })
    );

    const maxRetries = 3;
    const delayMs = 1500;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));

      try {
        const response = await ObjectsService.getObjectRidesByQuery(
          { accountId, objectId },
          params
        );

        if (response.status === 200) {
          const results = response.data.results || [];
          dispatch(
            updateObjectRidesSilently({
              results,
              rowCount: response.data.rowCount || 0
            })
          );

          if (
            rideIdToFind &&
            results.some(
              (ride: { uniqueId?: string }) => ride.uniqueId === rideIdToFind
            )
          ) {
            break;
          }
        }
      } catch (error) {
        console.error("Error refreshing rides after manual create:", error);
      }
    }
  };

  const onSubmitHandler = async (
    values: ICreateManualRideFormValues,
    formikHelpers: FormikHelpers<ICreateManualRideFormValues>
  ) => {
    formikHelpers.setSubmitting(true);

    const { type, payload } = await dispatch(
      createObjectRideThunk({
        accountId,
        objectId,
        data: transformCreateManualRideForApi(values)
      })
    );

    if (type === "objects/createObjectRideThunk/fulfilled") {
      const locationHeader =
        (payload as any)?.headers?.location ||
        (payload as any)?.headers?.Location;
      const rideId = extractRideIdFromLocationHeader(locationHeader);

      onClose();
      formikHelpers.resetForm();
      await refreshRides(rideId);
    }

    formikHelpers.setSubmitting(false);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createManualRideValidationSchema(t)}
      onSubmit={onSubmitHandler}
      validateOnMount
      enableReinitialize
    >
      <InnerForm open={open} onClose={handleClose} />
    </Formik>
  );
};

export default AddManualRideForm;
